import { CloseIcon } from '@chakra-ui/icons'
import { Box, Flex, IconButton, Text, VStack } from '@chakra-ui/react'
import { getDateString, PracticeVisit, PracticeVisitDraft, WithId } from '@hb/shared'
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { practiceSelectField } from '../../collections/fields/practice'
import { Size } from '../../hooks/useResizeObserver'
import { StandaloneInput } from '../forms'
import { VisitDraftPreview } from '../../charting/Visits/VisitView/VisitDraftPreview'
import { VisitDraftViewModal } from '../../charting/Visits/VisitView/VisitDraftViewModal'
import { VisitPreview } from '../../charting/Visits/VisitView/VisitPreview'
import { VisitViewModal } from '../../charting/Visits/VisitView/VisitViewModal'
import { SearchUsersRef } from '../Users/SearchUsers'
import { SelectUser } from '../Users/SelectUser'
import { useVisitsCalendar } from './context'
import { EventBoxType } from './types'

const NEW_VISIT_WIDTH = 300
const NEW_VISIT_HEIGHT = 200
const VIEW_VISIT_WIDTH = 300
const VIEW_VISIT_HEIGHT = 270
const VIEW_VISIT_DRAFT_WIDTH = 300
const VIEW_VISIT_DRAFT_HEIGHT = 300

// no need to display practice select if practiceId is provided
const getBoxSize = (type: EventBoxType) => {
  switch (type) {
    case 'new-visit':
      return { width: NEW_VISIT_WIDTH, height: NEW_VISIT_HEIGHT }
    case 'view-visit':
      return { width: VIEW_VISIT_WIDTH, height: VIEW_VISIT_HEIGHT }
    case 'view-visit-draft':
      return { width: VIEW_VISIT_DRAFT_WIDTH, height: VIEW_VISIT_DRAFT_HEIGHT }
    default:
      return { width: VIEW_VISIT_WIDTH, height: VIEW_VISIT_HEIGHT }
  }
}

const TrianglePointer = ({
  isRenderToLeft,
  x,
  y,
  triangleY,
  visible,
}: {
  isRenderToLeft: boolean
  x: number
  y: number
  triangleY: number
  visible: boolean
}) => (
  <Box
    as="svg"
    opacity={visible ? 1 : 0}
    transition={`opacity 200ms ease-in-out ${visible ? '100ms' : '0ms'}, transform 200ms ease-in-out`}
    position="absolute"
    top={0}
    left={0}
    // top={`${y + triangleY}px`}
    // left={isRenderToLeft ? `${x + CONTENT_WIDTH - 2}px` : `${x - 12}px`}
    width="12px"
    height="20px"
    zIndex={2}
    transform={`translate(${isRenderToLeft ? x + NEW_VISIT_WIDTH - 3 : x - 10}px, ${y + triangleY}px)`}
    style={{ filter: `drop-shadow(${isRenderToLeft ? '2px' : '-2px'} 0 2px rgba(0, 0, 0, 0.1))` }}>
    <polygon points={!isRenderToLeft ? '2,10 12,0 12,20' : '12,10 2,0 2,20'} fill="#f7f7f7" />
  </Box>
)

const OverlayContentContainer = ({
  children,
  isOpen,
  contentBox,
}: PropsWithChildren<{
  isOpen: boolean
  contentBox: OverlayContentBox
}>) => {
  const { position, arrowY, isRenderToLeft, width, height } = contentBox
  const { x, y } = position
  return (
    <>
      <Box
        position="absolute"
        top={0}
        left={0}
        bg="white"
        onClick={e => e.stopPropagation()}
        transform={`translate(${x}px, ${y}px)`}
        borderRadius={4}
        pointerEvents={isOpen ? 'auto' : 'none'}
        opacity={isOpen ? 1 : 0}
        transition={`opacity 200ms ease-in-out ${isOpen ? '100ms' : '0ms'}, transform 200ms ease-in-out`}
        boxShadow="md"
        w={width}
        zIndex={1}
        overflowY="auto"
        h={height}>
        {children}
      </Box>
      <TrianglePointer
        isRenderToLeft={isRenderToLeft}
        x={x}
        y={y}
        triangleY={arrowY}
        visible={isOpen}
      />
    </>
  )
}

const NewVisitBox = ({
  contentBox,
  date,
  onClose,
}: {
  contentBox: OverlayContentBox
  date: string | null
  onClose: () => void
}) => {
  const { onSelectNewVisit, practiceId } = useVisitsCalendar()
  const [patientId, setPatientId] = useState<string | null>(null)
  const [manualPracticeId, setManualPracticeId] = useState<string | null>(null)
  const searchUsersRef = useRef<SearchUsersRef>(null)
  useEffect(() => {
    if (!date) {
      setPatientId(null)
    } else {
      searchUsersRef.current?.open()
    }
  }, [date])
  return (
    <OverlayContentContainer isOpen={!!date} contentBox={contentBox}>
      <Flex
        borderTopRadius={4}
        bg="gray.50"
        py={1}
        pl={3}
        pr={2}
        borderBottom="1px solid"
        borderColor="gray.200"
        align="center">
        <Text fontWeight={500} fontFamily="Open Sans">
          {date ? `Create Event on ${getDateString(date, 'short')}` : 'Create Event'}
        </Text>
        <IconButton
          borderRadius="full"
          ml="auto"
          size="sm"
          aria-label="Close"
          icon={<CloseIcon />}
          onClick={onClose}
        />
      </Flex>
      <VStack p={2}>
        {!practiceId ? (
          <StandaloneInput
            field={practiceSelectField}
            onChange={setManualPracticeId}
            theme="detailed"
            value={manualPracticeId}
          />
        ) : null}
        <SelectUser
          patientId={patientId}
          ref={searchUsersRef}
          onSelect={id => {
            const visitPracticeId = practiceId ?? manualPracticeId
            if (!visitPracticeId || !date) {
              return
            }
            onSelectNewVisit({
              patientId: id,
              practiceId: visitPracticeId,
              visitType: 'encounter',
              date,
            })
          }}
          placeholder="Select patient"
          userTypeName="patient"
        />
      </VStack>
    </OverlayContentContainer>
  )
}

const ViewVisitBox = ({
  contentBox,
  visit,
  onClose,
}: {
  contentBox: OverlayContentBox
  visit: WithId<PracticeVisit> | null
  onClose: () => void
}) => {
  const [fullViewVisit, setFullViewVisit] = useState<WithId<PracticeVisit> | null>(null)

  const handleOpenModal = useCallback(
    (visit: WithId<PracticeVisit>) => {
      setFullViewVisit(visit)
      onClose()
    },
    [onClose],
  )
  const handleCloseModal = useCallback(() => {
    setFullViewVisit(null)
    onClose()
  }, [onClose])

  return (
    <>
      <OverlayContentContainer isOpen={!!visit} contentBox={contentBox}>
        <Flex
          borderTopRadius={4}
          bg="gray.50"
          py={1}
          pl={3}
          pr={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          align="center">
          <Text fontWeight={500} fontFamily="Open Sans">
            Visit Details
          </Text>
          <IconButton
            borderRadius="full"
            ml="auto"
            size="sm"
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        {visit ? <VisitPreview visit={visit} onOpenModal={() => handleOpenModal(visit)} /> : null}
      </OverlayContentContainer>
      {fullViewVisit ? <VisitViewModal visit={fullViewVisit} onClose={handleCloseModal} /> : null}
    </>
  )
}

const ViewVisitDraftBox = ({
  contentBox,
  visitDraft,
  onClose,
}: {
  contentBox: OverlayContentBox
  visitDraft: WithId<PracticeVisitDraft> | null
  onClose: () => void
}) => {
  const [fullViewVisitDraft, setFullViewVisitDraft] = useState<WithId<PracticeVisitDraft> | null>(
    null,
  )

  const handleOpenModal = useCallback(() => {
    setFullViewVisitDraft(visitDraft)
    onClose()
  }, [visitDraft, onClose])

  const handleCloseModal = useCallback(() => {
    setFullViewVisitDraft(null)
    onClose()
  }, [onClose])

  return (
    <>
      <OverlayContentContainer isOpen={!!visitDraft} contentBox={contentBox}>
        <Flex
          borderTopRadius={4}
          bg="gray.50"
          py={1}
          pl={3}
          pr={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          align="center">
          <Text fontWeight={500} fontFamily="Open Sans">
            Draft Visit Details
          </Text>
          <IconButton
            borderRadius="full"
            ml="auto"
            size="sm"
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        {visitDraft ? (
          <VisitDraftPreview visitDraft={visitDraft} onOpenModal={handleOpenModal} />
        ) : null}
      </OverlayContentContainer>
      {fullViewVisitDraft ? (
        <VisitDraftViewModal draft={fullViewVisitDraft} onClose={handleCloseModal} />
      ) : null}
    </>
  )
}

export interface OverlayContentBox {
  handleOpen: (dateEl: HTMLElement) => void
  position: { x: number; y: number }
  width: number
  height: number
  arrowY: number
  isRenderToLeft: boolean
}
const useOverlayContentBox = (
  type: EventBoxType,
  practiceId: string | null,
  containerSize: Size,
  offset: { x: number; y: number },
): OverlayContentBox => {
  const [{ x, y }, setPosition] = useState({ x: 0, y: 0 })
  const [isRenderToLeft, setIsRenderToLeft] = useState(false)
  const [arrowY, setArrowY] = useState(0)

  const boxSize = useMemo(() => getBoxSize(type), [type])
  const { width, height } = boxSize

  const handleOpen = useCallback(
    (dateEl: HTMLElement) => {
      const rect = dateEl.getBoundingClientRect()
      const left = rect.left
      const shouldRenderToLeft = left > width * 1.5 + offset.x
      setIsRenderToLeft(shouldRenderToLeft)

      const x = Math.min(
        containerSize.width - width - 10,
        Math.max(
          0,
          shouldRenderToLeft ? left - width - offset.x - 10 : left + rect.width - offset.x + 10,
        ),
      )
      const y = Math.min(
        containerSize.height - height - 10,
        Math.max(10, rect.y + rect.height / 2 - offset.y - height / 2),
      )

      // Calculate triangle Y position (middle of date box relative to overlay content)
      const dateBoxMiddleY = rect.y + rect.height / 2 - offset.y
      const triangleYPosition = dateBoxMiddleY - y
      setArrowY(triangleYPosition)

      setPosition({
        x,
        y,
      })
    },
    [containerSize, offset, width, height],
  )

  return {
    handleOpen,
    position: { x, y },
    arrowY,
    isRenderToLeft,
    width,
    height,
  }
}

export const EventOverlay = ({
  containerSize,
  screenWidth,
  screenHeight,
}: {
  containerSize: Size
  screenWidth: number
  screenHeight: number
}) => {
  const { createEventOverlayRef: ref } = useVisitsCalendar()

  const [newVisitDate, setNewVisitDate] = useState<string | null>(null)
  const [viewedVisit, setViewedVisit] = useState<WithId<PracticeVisit> | null>(null)
  const [viewedVisitDraft, setViewedVisitDraft] = useState<WithId<PracticeVisitDraft> | null>(null)
  const visible = useMemo(
    () => !!newVisitDate || !!viewedVisit || !!viewedVisitDraft,
    [newVisitDate, viewedVisit, viewedVisitDraft],
  )
  const handleClose = useCallback(() => {
    setNewVisitDate(null)
    setViewedVisit(null)
    setViewedVisitDraft(null)
  }, [])

  const { practiceId } = useVisitsCalendar()
  const offset = useMemo(
    () => ({
      x: (screenWidth - containerSize.width) / 2,
      y: (screenHeight - containerSize.height) / 2,
    }),
    [screenWidth, screenHeight, containerSize],
  )
  const newVisitBox = useOverlayContentBox('new-visit', practiceId, containerSize, offset)
  const viewVisitBox = useOverlayContentBox('view-visit', practiceId, containerSize, offset)
  const viewVisitDraftBox = useOverlayContentBox(
    'view-visit-draft',
    practiceId,
    containerSize,
    offset,
  )
  const handleOpenNewVisit = useCallback(
    (dateEl: HTMLElement, date: string) => {
      setNewVisitDate(date)
      newVisitBox.handleOpen(dateEl)
    },
    [newVisitBox],
  )

  const handleOpenVisit = useCallback(
    (dateEl: HTMLElement, visit: WithId<PracticeVisit>) => {
      setViewedVisit(visit)
      viewVisitBox.handleOpen(dateEl)
    },
    [viewVisitBox],
  )

  const handleOpenVisitDraft = useCallback(
    (dateEl: HTMLElement, visitDraft: WithId<PracticeVisitDraft>) => {
      setViewedVisitDraft(visitDraft)
      viewVisitDraftBox.handleOpen(dateEl)
    },
    [viewVisitDraftBox],
  )

  useImperativeHandle(ref, () => ({
    openNewVisit: handleOpenNewVisit,
    openVisit: handleOpenVisit,
    openVisitDraft: handleOpenVisitDraft,
    close: handleClose,
  }))
  return (
    <Box
      position="absolute"
      top="0"
      left="0"
      width="100%"
      zIndex={2}
      height="100%"
      onClick={handleClose}
      bg="blackAlpha.200"
      pointerEvents={visible ? 'auto' : 'none'}
      opacity={visible ? 1 : 0}
      transition="opacity 200ms ease-in-out">
      <NewVisitBox
        contentBox={newVisitBox}
        date={newVisitDate}
        onClose={() => setNewVisitDate(null)}
      />
      <ViewVisitBox
        contentBox={viewVisitBox}
        visit={viewedVisit}
        onClose={() => setViewedVisit(null)}
      />
      <ViewVisitDraftBox
        contentBox={viewVisitDraftBox}
        visitDraft={viewedVisitDraft}
        onClose={() => setViewedVisitDraft(null)}
      />
    </Box>
  )
}
